import React from 'react';
import { Box } from '@mui/material';
import { SettingsInput, SettingsToggle, SettingsSection } from './SettingsPrimitives';
import DatabaseOptionsSettings from './DatabaseOptionsSettings';
import FileUploadSettings from './FileUploadSettings';
import ValidationSection from './FieldValidationEditors';

/**
 * Expandable per-field settings: key, help text, prefill/placeholder, required,
 * options source, file-upload rules and validation. Shared by both builder
 * variants — `isPublic` only changes the container framing.
 */
export default function FieldSettingsPanel({
  field,
  index,
  onUpdate,
  isDisplayField,
  isPublic,
  palette,
  theme,
  validations,
  onValidationsChange,
}) {
  return (
    <Box
      className="form-field-settings"
      sx={{
        borderTop: isPublic ? 'none' : `1px solid ${palette.border}`,
        backgroundColor: palette.surfaceSubtle,
        px: 1.5,
        py: 1.5,
        mt: isPublic ? 1.5 : 0,
        borderRadius: isPublic ? '12px' : 0,
        border: isPublic ? `1px solid ${palette.border}` : undefined,
        display: 'flex',
        flexDirection: 'column',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <SettingsSection first palette={palette}>
        <SettingsInput
          label="Field key"
          value={field.key}
          onChange={(val) => onUpdate(index, { key: val.replace(/\s+/g, '_').toLowerCase() })}
          placeholder="field_key"
          mono
          palette={palette}
          theme={theme}
        />
        {!isDisplayField && (
          <>
            <SettingsInput
              label="Help text"
              value={field.helpText || ''}
              onChange={(val) => onUpdate(index, { helpText: val })}
              placeholder="Optional help text"
              palette={palette}
              theme={theme}
            />
            {['text', 'number', 'date', 'time'].includes(field.type) && (
              <SettingsInput
                label="Prefill value"
                value={field.defaultValue || ''}
                onChange={(val) => onUpdate(index, { defaultValue: val })}
                placeholder={
                  field.type === 'date'
                    ? 'YYYY-MM-DD'
                    : field.type === 'time'
                      ? 'HH:mm'
                      : 'Default answer (optional)'
                }
                type={field.type === 'number' ? 'number' : 'text'}
                palette={palette}
                theme={theme}
              />
            )}
            {['text', 'number', 'rich_text', 'list'].includes(field.type) && (
              <SettingsInput
                label="Placeholder"
                value={field.placeholder || ''}
                onChange={(val) => onUpdate(index, { placeholder: val })}
                placeholder="Hint text shown in the empty field"
                palette={palette}
                theme={theme}
              />
            )}
            <SettingsToggle
              label="Required"
              checked={field.required || false}
              onChange={(val) => onUpdate(index, { required: val })}
              palette={palette}
            />
            {field.type === 'text' && (
              <SettingsToggle
                label="Multiline"
                checked={field.multiline || false}
                onChange={(val) => onUpdate(index, { multiline: val })}
                palette={palette}
              />
            )}
          </>
        )}
      </SettingsSection>

      {['dropdown', 'radio', 'checkbox'].includes(field.type) && (
        <SettingsSection title="Options source" palette={palette}>
          <DatabaseOptionsSettings
            field={field}
            onUpdate={(updates) => onUpdate(index, updates)}
            palette={palette}
            theme={theme}
          />
        </SettingsSection>
      )}

      {field.type === 'file_upload' && (
        <SettingsSection title="File upload" palette={palette}>
          <FileUploadSettings field={field} onUpdate={(updates) => onUpdate(index, updates)} palette={palette} theme={theme} />
        </SettingsSection>
      )}

      {!isDisplayField && (
        <SettingsSection title="Validation" palette={palette}>
          <ValidationSection
            field={field}
            validations={validations}
            onValidationsChange={onValidationsChange}
            palette={palette}
            theme={theme}
          />
        </SettingsSection>
      )}
    </Box>
  );
}
